import { type FC, type MouseEventHandler, type ReactElement, type ReactNode } from 'react';
import classnames from 'classnames';

import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { SeoLink } from 'dibs-elements/exports/SeoLink.tsx';
import ChevronRight from 'dibs-icons/exports/ChevronRight.ts';

import { viewAll } from '../../pdpMessages.tsx';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

import dibsCss from 'dibs-css';
import styles from './PdpSharedFeedCarouselFrame.module.css';

export const PdpSharedFeedCarouselViewAllLabel: FC = () => (
    <span className={classnames(dibsCss.inlineFlex, dibsCss.itemsCenter, dibsCss.gapXxxsmall)}>
        {viewAll}
        <ChevronRight variation="20" />
    </span>
);

export const PdpSharedFeedCarouselFrame: FC<{
    title: ReactElement;
    children: ReactNode;
    viewAllLink?: string;
    viewAllComponent?: ReactElement;
    handleViewAllClick?: MouseEventHandler;
    viewAllTarget?: string;
    dataTn?: string;
    compact?: boolean;
}> = ({
    title,
    children,
    viewAllLink,
    viewAllComponent,
    handleViewAllClick,
    viewAllTarget,
    dataTn,
    compact = false,
}) => {
    const { isMobile } = useServerVarsContext();
    const isCompactMobile = !!isMobile && compact;
    // Full width modules replace the card border with inset hairlines above and below.
    const isMobileBleed = !!isMobile && !compact;
    const showCardChrome = !isMobile || isCompactMobile;

    const bleedHairline = isMobileBleed ? (
        <div className={classnames(dibsCss.h1px, dibsCss.bgDolphin, dibsCss.mxSmall)} aria-hidden />
    ) : null;

    return (
        <div
            className={classnames(
                styles.frame,
                dibsCss.relative,
                dibsCss.flex,
                dibsCss.flexCol,
                dibsCss.bgWhite,
                {
                    [styles.frameMobile]: isMobileBleed,
                    [styles.frameCompactMobile]: isCompactMobile,
                    [styles.frameCompactResp]: !isMobile && compact,
                    [dibsCss.border]: showCardChrome,
                    [dibsCss.borderSolid]: showCardChrome,
                    [dibsCss.borderDolphin]: showCardChrome,
                    [dibsCss.roundedXsmall]: showCardChrome,
                    [dibsCss.overflowHidden]: isCompactMobile,
                    'primary-viewport-width': isMobileBleed,
                }
            )}
            data-tn={dataTn}
        >
            {bleedHairline}
            <div
                className={classnames(dibsCss.flex, dibsCss.flexCol, {
                    [dibsCss.ptSmall]: isMobile,
                    [dibsCss.pbSmall]: isMobile,
                    [dibsCss.pMedium]: !isMobile,
                    [dibsCss.gapSmall]: isMobile,
                    [dibsCss.gapMedium]: !isMobile,
                })}
            >
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.justifyBetween,
                        dibsCss.gapSmall,
                        {
                            [dibsCss.pxSmall]: isMobile,
                        }
                    )}
                >
                    <HeadingLevel>
                        {Heading => (
                            <Heading
                                className={classnames(dibsCss.m0, dibsCss.minW0, dibsCss.flex1, {
                                    [dibsCss.sassyFontHeaderXLarge]: !isMobile,
                                    [dibsCss.sassyFontHeaderMedium]: isMobile,
                                })}
                            >
                                {title}
                            </Heading>
                        )}
                    </HeadingLevel>
                    {viewAllLink && (
                        <SeoLink
                            buyerLinkType="standardLinkNoUnderline"
                            className={classnames(
                                dibsCss.sassyFontBodyMediumHeavy,
                                dibsCss.inlineFlex,
                                dibsCss.itemsCenter,
                                dibsCss.gapXxxsmall,
                                dibsCss.maxW50,
                                dibsCss.shrink0
                            )}
                            onClick={handleViewAllClick}
                            target={viewAllTarget}
                            linkData={{
                                path: viewAllLink,
                                isLinkable: false,
                            }}
                            dataTn={`${dataTn || 'view-all'}-link`}
                        >
                            {viewAllComponent || <PdpSharedFeedCarouselViewAllLabel />}
                        </SeoLink>
                    )}
                </div>
                {children}
            </div>
            {bleedHairline}
        </div>
    );
};
